import React, { useState } from 'react';
import { Upload, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEmployees } from '../hooks/useEmployees';

interface NewEmployeeFormData {
  // Personal Info
  fullName: string;
  email: string;
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  mobilePhone: string;
  laborCost: string;
  
  // Permissions
  makeAdministrator: boolean;
  permissionLevel: 'employee' | 'worker' | 'dispatcher' | 'manager';
  
  // Features
  schedule: boolean;
  notes: boolean;
  showPricing: boolean;
  jobCosting: boolean;
  clientsAndProperties: boolean;
  requests: boolean;
  quotes: boolean;
  jobs: boolean;
  invoices: boolean;
  reports: boolean;
  
  // Communications
  emailSubscriptions: boolean;
  invitationLanguage: 'english' | 'spanish';
}

export default function CreateEmployee() {
  const { addEmployee } = useEmployees();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<NewEmployeeFormData>({
    fullName: '',
    email: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
    mobilePhone: '',
    laborCost: '$0.00',
    makeAdministrator: false,
    permissionLevel: 'employee',
    schedule: true,
    notes: true,
    showPricing: false,
    jobCosting: false,
    clientsAndProperties: true,
    requests: true,
    quotes: false,
    jobs: true,
    invoices: false,
    reports: false,
    emailSubscriptions: true,
    invitationLanguage: 'english'
  });

  const handleInputChange = (field: keyof NewEmployeeFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Extract hourly rate from labor cost
      const laborCostNumber = parseFloat(formData.laborCost.replace('$', '')) || 0;
      
      // Map permission level to role
      const roleMapping = {
        employee: 'Employee',
        worker: 'Worker',
        dispatcher: 'Dispatcher',
        manager: formData.makeAdministrator ? 'Admin' : 'Manager'
      };

      const role = roleMapping[formData.permissionLevel];

      // Create the employee first
      await addEmployee({
        name: formData.fullName,
        email: formData.email,
        phone: formData.mobilePhone,
        role: role,
        status: 'active' as const,
        hireDate: new Date().toISOString().split('T')[0],
        hourlyRate: laborCostNumber,
        userId: null
      });

      // See #460 — permissions record is collected but not yet persisted
      const permissionsData = {
        streetAddress: formData.streetAddress,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode,
        country: formData.country,
        makeAdministrator: formData.makeAdministrator,
        permissionLevel: formData.permissionLevel,
        schedule: formData.schedule,
        notes: formData.notes,
        showPricing: formData.showPricing,
        jobCosting: formData.jobCosting,
        clientsAndProperties: formData.clientsAndProperties,
        requests: formData.requests,
        quotes: formData.quotes,
        jobs: formData.jobs,
        invoices: formData.invoices,
        reports: formData.reports,
        emailSubscriptions: formData.emailSubscriptions,
        invitationLanguage: formData.invitationLanguage
      };

      console.log('Employee permissions to store:', permissionsData);
      
      navigate('/employees');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const ToggleSwitch: React.FC<{ enabled: boolean; onChange: (enabled: boolean) => void }> = ({ enabled, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-indigo-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const RadioButton: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`h-4 w-4 rounded-full border-2 ${
        checked ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 bg-white'
      }`}
    >
      {checked && <div className="h-2 w-2 rounded-full bg-white m-0.5" />}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">New User</h1>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-8">
        {/* Personal Info Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Personal Info</h2>
          
          {/* Profile Image Upload */}
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center mr-4">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <button type="button" className="text-indigo-600 font-medium">Upload Image</button>
          </div>

          {/* Form Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <input
              type="text"
              placeholder="Full name"
              required
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Street address"
              value={formData.streetAddress}
              onChange={(e) => handleInputChange('streetAddress', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <input
              type="email"
              placeholder="Email address"
              required
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Mobile phone number (if applicable)"
              value={formData.mobilePhone}
              onChange={(e) => handleInputChange('mobilePhone', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <input
              type="text"
              placeholder="Province"
              value={formData.province}
              onChange={(e) => handleInputChange('province', e.target.value)}
              className="p-3 border border-gray-300 rounded-md"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Postal code"
                value={formData.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                className="p-3 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                placeholder="Country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="p-3 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          {/* Labor Cost */}
          <div className="flex items-center gap-2 mb-4">
            <label className="font-medium">Labor cost</label>
            <HelpCircle className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">Employee cost</span>
            <input
              type="text"
              value={formData.laborCost}
              onChange={(e) => handleInputChange('laborCost', e.target.value)}
              className="w-20 p-2 border border-gray-300 rounded-md"
            />
            <span className="text-sm text-gray-600">per hour</span>
          </div>
        </section>

        {/* Permissions Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Permissions</h2>
          
          <div className="mb-6">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.makeAdministrator}
                onChange={(e) => handleInputChange('makeAdministrator', e.target.checked)}
                className="w-4 h-4"
              />
              <span className="font-medium">Make administrator</span>
            </label>
            <p className="text-sm text-gray-600 ml-7">
              This grants them access to everything within the account — including billing, reports, client list, adding or altering permissions, etc.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="font-medium mb-3">Preset permission levels</h3>
            <p className="text-sm text-gray-600 mb-4">
              Start with a preset permission level, and customize further as needed.
            </p>
            
            <div className="space-y-3">
              <label className="flex items-start gap-3">
                <RadioButton
                  checked={formData.permissionLevel === 'employee'}
                  onChange={() => handleInputChange('permissionLevel', 'employee')}
                />
                <div>
                  <div className="font-medium">Employee</div>
                  <div className="text-sm text-gray-600">Can view their schedule, mark work complete, and track their time</div>
                </div>
              </label>
              
              <label className="flex items-start gap-3">
                <RadioButton
                  checked={formData.permissionLevel === 'worker'}
                  onChange={() => handleInputChange('permissionLevel', 'worker')}
                />
                <div>
                  <div className="font-medium">Worker</div>
                  <div className="text-sm text-gray-600">Can schedule clients, add jobs, including pricing details</div>
                </div>
              </label>
              
              <label className="flex items-start gap-3">
                <RadioButton
                  checked={formData.permissionLevel === 'dispatcher'}
                  onChange={() => handleInputChange('permissionLevel', 'dispatcher')}
                />
                <div>
                  <div className="font-medium">Dispatcher</div>
                  <div className="text-sm text-gray-600">Can add job, client and client details. Recommended for team leads</div>
                </div>
              </label>
              
              <label className="flex items-start gap-3">
                <RadioButton
                  checked={formData.permissionLevel === 'manager'}
                  onChange={() => handleInputChange('permissionLevel', 'manager')}
                />
                <div>
                  <div className="font-medium">Manager</div>
                  <div className="text-sm text-gray-600">Can manage all areas excluding reports and payroll. Recommended for management</div>
                </div>
              </label>
            </div>
          </div>
        </section>

        {/* Feature Permissions */}
        <section className="space-y-6">
          {/* Schedule */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Schedule</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>✓ View their own schedule</div>
                <div>✓ View and complete their own schedule</div>
                <div>• Edit their own schedule</div>
                <div>• Edit everyone's schedule</div>
                <div>• Edit and delete everyone's schedule</div>
              </div>
            </div>
            <ToggleSwitch
              enabled={formData.schedule}
              onChange={(enabled) => handleInputChange('schedule', enabled)}
            />
          </div>

          {/* Notes */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Notes</h3>
              <p className="text-sm text-gray-600">
                Includes limited access Jobber. You can hide notes for a feature by turning off permissions for that feature.
              </p>
              <div className="text-sm text-gray-600 space-y-1">
                <div>• View notes on jobs and visits only</div>
                <div>• View all notes</div>
                <div>✓ View and edit all</div>
                <div>• View, edit, and delete all</div>
              </div>
            </div>
            <ToggleSwitch
              enabled={formData.notes}
              onChange={(enabled) => handleInputChange('notes', enabled)}
            />
          </div>

          {/* Show pricing */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Show pricing</h3>
              <p className="text-sm text-gray-600">
                Allows editing of quotes, invoices, and line items on jobs.
              </p>
            </div>
            <ToggleSwitch
              enabled={formData.showPricing}
              onChange={(enabled) => handleInputChange('showPricing', enabled)}
            />
          </div>

          {/* Job costing */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Job costing</h3>
              <p className="text-sm text-gray-600">
                Show job profit by tracking revenue and costs from the items and labor.
              </p>
              <p className="text-sm text-gray-600">
                Turn on show pricing and jobs to give access to job costing.
              </p>
            </div>
            <ToggleSwitch
              enabled={formData.jobCosting}
              onChange={(enabled) => handleInputChange('jobCosting', enabled)}
            />
          </div>

          {/* Clients and properties */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Clients and properties</h3>
              <p className="text-sm text-gray-600">Includes access to all client custom fields.</p>
              <div className="text-sm text-gray-600 space-y-1">
                <div>• View client name and address only</div>
                <div>✓ View full client and property info</div>
                <div>• View and edit full client and property info</div>
                <div>• View, edit, and delete full client and property info</div>
                <div>• Show clients on their Jobber menu</div>
              </div>
            </div>
            <ToggleSwitch
              enabled={formData.clientsAndProperties}
              onChange={(enabled) => handleInputChange('clientsAndProperties', enabled)}
            />
          </div>

          {/* Requests */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Requests</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>✓ View only</div>
                <div>• View, create, and edit</div>
                <div>• View, create, edit, and delete</div>
                <div>• Show requests on their Jobber menu</div>
              </div>
            </div>
            <ToggleSwitch
              enabled={formData.requests}
              onChange={(enabled) => handleInputChange('requests', enabled)}
            />
          </div>

          {/* Quotes */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Quotes</h3>
            </div>
            <ToggleSwitch
              enabled={formData.quotes}
              onChange={(enabled) => handleInputChange('quotes', enabled)}
            />
          </div>

          {/* Jobs */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Jobs</h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>✓ View only</div>
                <div>• View, create, and edit</div>
                <div>• View, create, edit, and delete</div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Select edit their own schedule to create and edit jobs.
              </p>
              <div className="text-sm text-gray-600">
                • Show jobs on their Jobber menu
              </div>
            </div>
            <ToggleSwitch
              enabled={formData.jobs}
              onChange={(enabled) => handleInputChange('jobs', enabled)}
            />
          </div>

          {/* Invoices */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Invoices</h3>
            </div>
            <ToggleSwitch
              enabled={formData.invoices}
              onChange={(enabled) => handleInputChange('invoices', enabled)}
            />
          </div>

          {/* Reports */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Reports</h3>
              <p className="text-sm text-gray-600">
                Users will only be able to see reports available to them based on their other permissions.
              </p>
            </div>
            <ToggleSwitch
              enabled={formData.reports}
              onChange={(enabled) => handleInputChange('reports', enabled)}
            />
          </div>
        </section>

        {/* Communications Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Communications</h2>
          
          <div className="mb-6">
            <h3 className="font-medium mb-2">Email subscriptions</h3>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.emailSubscriptions}
                onChange={(e) => handleInputChange('emailSubscriptions', e.target.checked)}
                className="w-4 h-4"
              />
              <span>Surveys</span>
            </label>
            <p className="text-sm text-gray-600 ml-7">
              Get notified when a customer fills out a survey.
            </p>
          </div>

          <div className="mb-6">
            <h3 className="font-medium mb-2">Invitation language</h3>
            <p className="text-sm text-gray-600 mb-3">
              The chosen language only applies to the invitation and cannot be changed once sent.
            </p>
            
            <div className="space-y-2">
              <label className="flex items-center gap-3">
                <RadioButton
                  checked={formData.invitationLanguage === 'english'}
                  onChange={() => handleInputChange('invitationLanguage', 'english')}
                />
                <span>English</span>
              </label>
              
              <label className="flex items-center gap-3">
                <RadioButton
                  checked={formData.invitationLanguage === 'spanish'}
                  onChange={() => handleInputChange('invitationLanguage', 'spanish')}
                />
                <span>Spanish</span>
              </label>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              You should have a translated to Spanish copy for non-admin users with this feature language set to Spanish.
            </p>
          </div>
        </section>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/employees')}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Creating User...' : 'Send User'}
          </button>
        </div>
          </form>
        </div>
      </div>
    </div>
  );
};
